// Pure runtime helpers behind the `<EntityListView>` shell.
//
// Extracted so the behaviour-critical logic — server-side data source, field-restriction
// filtering, export request shaping — can be characterization-tested directly against a mock
// service with the REAL grid utils, instead of through DataGridPremium (unreliable in jsdom).
// The shell uses these exact functions, so the tests exercise production code, not a copy.

import type {
  GridColDef,
  GridDataSource,
  GridRowId,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import {
  mapGridParamsToAbp,
  toGridResponse,
  buildAggregateFieldsParam,
  buildFilterParams,
  buildSortingString,
  type AbpPagedRequest,
  type AbpPagedResponse,
  type AbpGroupedResponse,
} from '@/lib/gridDataSourceUtils';

/** The read surface the list data source needs from an entity service. */
export interface ListDataService {
  getList(params: AbpPagedRequest): Promise<AbpPagedResponse<any>>;
  getGroupedList(params: AbpPagedRequest): Promise<AbpGroupedResponse>;
  getAggregates(params: AbpPagedRequest): Promise<Record<string, unknown>>;
}

/**
 * Build the server-side {@link GridDataSource} for an entity list: ABP param mapping, server-side
 * row grouping (grouped-list endpoint with cascading equality filters), and server-side
 * aggregation. Identical to the logic previously inlined into every generated ListView.
 */
export function createEntityListDataSource(
  service: ListDataService,
  /**
   * Fixed API params merged into every fetch — the deep-link filter channel (`?filter.<field>`).
   * Sent straight to the server so it works for any filterable API field, including ones the grid
   * has no column for (id-exact filters); the grid's own filter model stays untouched.
   */
  extraParams?: Record<string, unknown>,
): GridDataSource {
  return {
    getRows: async (params) => {
      const { groupKeys = [], groupFields = [] } = params;
      const abpParams = { ...mapGridParamsToAbp(params), ...extraParams };

      // Server-side row grouping: when grouping is active and we're at a group level,
      // call the grouped-list endpoint to get distinct values with counts.
      if (groupFields.length > 0 && groupKeys.length < groupFields.length) {
        for (let i = 0; i < groupKeys.length; i++) {
          const field = groupFields[i];
          abpParams[field] = String(groupKeys[i]);
          abpParams[`${field}Operator`] = 'equals';
        }
        abpParams['groupByField'] = groupFields[groupKeys.length];

        const response = await service.getGroupedList(abpParams);
        return {
          rows: response.items.map((g) => ({
            id: `group-${groupFields[groupKeys.length]}-${g.groupKey}`,
            _groupKey: g.groupKey,
            _childCount: g.count,
          })),
          rowCount: response.totalCount,
        };
      }

      // Leaf level (or no grouping): apply accumulated group key filters and fetch entity rows.
      for (let i = 0; i < groupKeys.length; i++) {
        const field = groupFields[i];
        abpParams[field] = String(groupKeys[i]);
        abpParams[`${field}Operator`] = 'equals';
      }

      const response = await service.getList(abpParams);
      const gridResponse = toGridResponse(response);

      // Server-side aggregation: if the grid requests aggregates, compute them server-side.
      const aggregationModel = (params as any).aggregationModel as Record<string, string> | undefined;
      const aggregateFieldsParam = buildAggregateFieldsParam(aggregationModel);
      if (aggregateFieldsParam) {
        const aggregateParams = { ...abpParams, aggregateFields: aggregateFieldsParam };
        const aggregateRow = await service.getAggregates(aggregateParams);
        return { ...gridResponse, aggregateRow };
      }

      return gridResponse;
    },
    getGroupKey: (row) => (row as any)._groupKey,
    getChildrenCount: (row) => (row as any)._childCount,
    getAggregatedValue: (row, field) => (row as any)[field] ?? null,
  };
}

/**
 * Resolve a grid row-selection model to concrete leaf-row ids for bulk actions.
 *
 * 'include' models return the explicitly selected ids. 'exclude' models (header
 * select-all with server-side data) are resolved against the currently loaded row
 * ids — bulk actions only ever operate on rows the user could actually see.
 * Server-side grouping rows (`group-*` ids) are never included.
 */
export function resolveSelectionIds(
  model: GridRowSelectionModel,
  loadedRowIds: readonly GridRowId[],
): string[] {
  const isLeaf = (id: GridRowId) => !String(id).startsWith('group-');
  if (model.type === 'include') {
    return [...model.ids].filter(isLeaf).map(String);
  }
  return loadedRowIds.filter((id) => isLeaf(id) && !model.ids.has(id)).map(String);
}

/**
 * Resolve selected ids to the row objects the grid currently holds, so a toolbar action can read
 * row fields (e.g. `status`) without a round-trip. An id whose row is no longer loaded (page
 * changed, refetch in flight) is dropped rather than yielding a hole — callers never see an
 * `undefined` row, and the selection ids stay the authority on WHAT was picked.
 */
export function resolveSelectedRows(
  selectedIds: readonly string[],
  getRow: (id: GridRowId) => GridValidRowModel | null | undefined,
): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  for (const id of selectedIds) {
    const row = getRow(id);
    if (row) rows.push(row);
  }
  return rows;
}

/**
 * What Edit does with the selection (Bulk-Edit-And-Actions.md §8.1): one row opens that record the way
 * clicking it would; several open the bulk edit dialog over them.
 */
export function editTarget(selectedIds: readonly string[]): { open: string } | 'bulk' {
  return selectedIds.length === 1 ? { open: selectedIds[0] } : 'bulk';
}

/**
 * The navigation state a list hands to the detail view it opens, so the detail view's Back
 * returns to the SAME list URL — query string included. The URL carries the list's pre-filter
 * (`filter.<field>` / `filterLabel.<field>`), so without the search part a filtered list would
 * come back unfiltered. Same `returnTo` convention the generated reference-cell links use.
 */
export function listReturnTo(pathname: string, search: string): { returnTo: string } {
  return { returnTo: `${pathname}${search}` };
}

/**
 * Whether the user is typing in the list's own quick filter or a header filter, which the
 * first-row auto-focus must not interrupt. An input outside the list does not count: the
 * command palette's input still holds focus while the palette closes after navigating here,
 * and unmounts right after, leaving focus on the body.
 */
export function isTypingInList(listRoot: Element | null, active: Element | null): boolean {
  return active instanceof HTMLInputElement && !!listRoot?.contains(active);
}

/** Field-restriction map from `useFieldRestrictions` (field name → restriction). */
export type FieldRestrictions = Map<string, string> | undefined;

/** Remove HIDDEN columns for the current user+entity. No-op when there are no restrictions. */
export function filterRestrictedColumns(columns: GridColDef[], restrictions: FieldRestrictions): GridColDef[] {
  if (!restrictions || restrictions.size === 0) return columns;
  return columns.filter((col) => restrictions.get(col.field) !== 'HIDDEN');
}

/** Minimal grid-state shape the export request needs. */
export interface ExportGridState {
  columns?: { columnVisibilityModel?: Record<string, boolean> };
  filter?: { filterModel?: any };
  sorting?: { sortModel?: any };
}

/**
 * Build the export request params from current grid state: only visible, non-action columns
 * (respecting the user's column-visibility toggles), plus active sort and filter. `now` is passed
 * in (not read from Date.now) so the shaping is deterministic and testable.
 */
export function buildExportRequestParams(
  state: ExportGridState,
  columns: GridColDef[],
  format: 'csv' | 'xlsx',
  now: number,
): Record<string, unknown> {
  const columnVisibility = state.columns?.columnVisibilityModel || {};
  const visibleColumns = columns
    .filter((c) => c.field && c.field !== '__check__' && c.field !== 'actions' && c.field !== '_rowActions')
    .filter((c) => columnVisibility[c.field] !== false) // false = explicitly hidden
    .map((c) => c.field)
    .join(',');

  const filterModel = state.filter?.filterModel;
  const sortModel = state.sorting?.sortModel;
  const params: Record<string, unknown> = { format, fields: visibleColumns, _t: now };
  if (sortModel?.length) {
    params.sorting = buildSortingString(sortModel);
  }
  if (filterModel?.items?.length) {
    Object.assign(params, buildFilterParams(filterModel));
  }
  if (filterModel?.quickFilterValues?.length) {
    params.filter = filterModel.quickFilterValues.join(' ');
  }
  return params;
}
